import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { login, clearError } from "../store/slices/authSlice";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useSnackbar } from "notistack";

const Login = () => {
  const { t } = useTranslation();
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const { loading, error } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const message = location.state?.message;
  const messageType = location.state?.type || "error";

  useEffect(() => {
    dispatch(clearError());
    return () => dispatch(clearError());
  }, [dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    let token = null;
    
    // Only execute reCAPTCHA if it's available
    if (executeRecaptcha) {
      token = await executeRecaptcha("login");
      if (!token) {
        enqueueSnackbar(t("auth.login.captchaRequired"), { variant: "error" });
        return;
      }
    }

    try {
      await dispatch(
        login({
          ...credentials,
          captchaToken: token,
        })
      ).unwrap();
      navigate("/");
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 400, width: "100%", textAlign: "center" }}>
        <Typography variant="h5" gutterBottom>
          {t("auth.login.title")}
        </Typography>

        {message && (
          <Alert severity={messageType} sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t("auth.login.error")}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label={t("auth.login.email")}
            type="email"
            margin="normal"
            value={credentials.email}
            onChange={(e) =>
              setCredentials({ ...credentials, email: e.target.value })
            }
            disabled={loading}
            required
          />
          <TextField
            fullWidth
            label={t("auth.login.password")}
            type="password"
            margin="normal"
            value={credentials.password}
            onChange={(e) =>
              setCredentials({ ...credentials, password: e.target.value })
            }
            disabled={loading}
            required
          />

          <Button
            fullWidth
            variant="contained"
            type="submit"
            sx={{ mt: 3 }}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              t("auth.login.loginButton")
            )}
          </Button>
        </form>
        <Typography variant="body2" sx={{ mt: 2 }}>
          {t("auth.login.noAccount")}{" "}
          <RouterLink
            to="/register"
            style={{ textDecoration: "none", color: "#38F2AF" }}
          >
            {t("auth.login.registerHere")}
          </RouterLink>
        </Typography>
      </Paper>
    </Box>
  );
};

export default Login;
